// src/pages/onboarding/OnboardingPlanPage.tsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import OnboardingLayout from "./OnboardingLayout";
import {
  Bot,
  getBotById,
  fetchChannels,
  BotChannel,
  ChannelType,
  fetchUsagePlans,
  UsagePlan,
  startBotCheckout,
  changeBotUsagePlan
} from "../../api/bots";
import { getStoredReferralCode } from "../../utils/referral";

type PlanFeature = {
  id: string;
  label: string;
  available: boolean;
};

const formatCurrency = (amountCents: number, currency: string) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2
  }).format(amountCents / 100);

const OnboardingPlanPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Display-only energy: show raw tokens / 10
  const formatEnergyDisplay = (tokens?: number | null): string => {
    if (tokens == null) {
      return t("botPlan.plans.unlimited", "Unlimited");
    }
    return (tokens / 10).toLocaleString("en-GB");
  };

  /**
   * Plan feature list:
   * - All plans have the same capabilities.
   * - We show Energy + "max" estimates if ALL Energy were used for a single task.
   * - Numbers are static per plan name (free / starter / growth / scale).
   */
  const planFeaturesConfig: Record<string, (plan: UsagePlan) => PlanFeature[]> =
    {
      free: (plan) => [
        {
          id: "energy",
          label: t(
            "botPlan.features.free.energy",
            "{{amount}} Energy per month",
            { amount: formatEnergyDisplay(plan.monthlyTokens) }
          ),
          available: true
        },
        {
          id: "messages",
          label: t(
            "botPlan.features.free.messages",
            "Up to 150 AI Assistant messages"
          ),
          available: true
        },
        {
          id: "pages",
          label: t(
            "botPlan.features.free.pages",
            "Up to 100 ingested pages"
          ),
          available: true
        },
        {
          id: "emails",
          label: t(
            "botPlan.features.free.emails",
            "Up to 20 emails"
          ),
          available: true
        },
        {
          id: "whatsappTemplates",
          label: t(
            "botPlan.features.free.whatsappTemplates",
            "No WhatsApp template messages (outbound)"
          ),
          available: false
        }
      ],
      starter: (plan) => [
        {
          id: "energy",
          label: t(
            "botPlan.features.starter.energy",
            "{{amount}} Energy per month",
            { amount: formatEnergyDisplay(plan.monthlyTokens) }
          ),
          available: true
        },
        {
          id: "messages",
          label: t(
            "botPlan.features.starter.messages",
            "Up to 3,000 AI Assistant messages"
          ),
          available: true
        },
        {
          id: "pages",
          label: t(
            "botPlan.features.starter.pages",
            "Up to 1,500 ingested pages"
          ),
          available: true
        },
        {
          id: "emails",
          label: t(
            "botPlan.features.starter.emails",
            "Up to 1,000 emails"
          ),
          available: true
        },
        {
          id: "whatsappTemplates",
          label: t(
            "botPlan.features.starter.whatsappTemplates",
            "Up to 200 WhatsApp template messages (outbound)"
          ),
          available: true
        }
      ],
      growth: (plan) => [
        {
          id: "energy",
          label: t(
            "botPlan.features.growth.energy",
            "{{amount}} Energy per month",
            { amount: formatEnergyDisplay(plan.monthlyTokens) }
          ),
          available: true
        },
        {
          id: "messages",
          label: t(
            "botPlan.features.growth.messages",
            "Up to 17,000 AI Assistant messages"
          ),
          available: true
        },
        {
          id: "pages",
          label: t(
            "botPlan.features.growth.pages",
            "Up to 6,000 ingested pages"
          ),
          available: true
        },
        {
          id: "emails",
          label: t(
            "botPlan.features.growth.emails",
            "Up to 5,000 emails"
          ),
          available: true
        },
        {
          id: "whatsappTemplates",
          label: t(
            "botPlan.features.growth.whatsappTemplates",
            "Up to 1,000 WhatsApp template messages (outbound)"
          ),
          available: true
        }
      ],
      scale: (plan) => [
        {
          id: "energy",
          label: t(
            "botPlan.features.scale.energy",
            "{{amount}} Energy per month",
            { amount: formatEnergyDisplay(plan.monthlyTokens) }
          ),
          available: true
        },
        {
          id: "messages",
          label: t(
            "botPlan.features.scale.messages",
            "Up to 65,000 AI Assistant messages"
          ),
          available: true
        },
        {
          id: "pages",
          label: t(
            "botPlan.features.scale.pages",
            "Up to 20,000 ingested pages"
          ),
          available: true
        },
        {
          id: "emails",
          label: t(
            "botPlan.features.scale.emails",
            "Up to 20,000 emails"
          ),
          available: true
        },
        {
          id: "whatsappTemplates",
          label: t(
            "botPlan.features.scale.whatsappTemplates",
            "Up to 5,000 WhatsApp template messages (outbound)"
          ),
          available: true
        }
      ],
      // Fallback if plan name isn't one of the keys above
      default: (plan) => [
        {
          id: "energy",
          label: t(
            "botPlan.features.default.energy",
            "{{amount}} Energy per month",
            { amount: formatEnergyDisplay(plan.monthlyTokens) }
          ),
          available: true
        }
      ]
    };

  const MOST_POPULAR_PLAN_KEY = "growth";

  const getPlanFeatures = (plan: UsagePlan): PlanFeature[] => {
    const key = plan.name.toLowerCase();
    const builder = planFeaturesConfig[key] ?? planFeaturesConfig.default;
    return builder(plan);
  };

  const isMostPopularPlan = (plan: UsagePlan): boolean =>
    plan.name.toLowerCase() === MOST_POPULAR_PLAN_KEY;

  const [bot, setBot] = useState<Bot | null>(null);
  const [channels, setChannels] = useState<BotChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [plans, setPlans] = useState<UsagePlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState<string | null>(null);

  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    const safeId = id;
    setLoading(true);
    setError(null);
    setPlansLoading(true);

    Promise.all([getBotById(safeId), fetchChannels(safeId), fetchUsagePlans()])
      .then(([botRes, channelsRes, plansRes]) => {
        setBot(botRes);
        setChannels(channelsRes);
        setPlans(plansRes);
        if (plansRes.length > 0) {
          setSelectedPlanId(plansRes[0].id);
        }
      })
      .catch((err: any) => {
        console.error("Failed to load onboarding plan data", err);
        setError(err?.message || t("botPlan.errors.loadFailed"));
      })
      .finally(() => {
        setLoading(false);
        setPlansLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const channelEnabled = (type: ChannelType) =>
    channels.some((c) => c.type === type);

  const bookingEnabled = bot?.useCalendar;
  const leadAdsEnabled =
    bot &&
    (bot.leadWhatsappMessages200 ||
      bot.leadWhatsappMessages500 ||
      bot.leadWhatsappMessages1000);

  const handleBack = () => {
    if (!id) return;
    navigate(`/onboarding/bots/${encodeURIComponent(id)}/lead-ads`);
  };

  const handleSelectPlan = (planId: string) => {
    setSelectedPlanId(planId);
    setSuccess(null);
  };

  const handleActivateOrChangePlan = async () => {
    if (!id || !bot || !selectedPlanId) return;

    const isActive = bot.status === "ACTIVE";

    setCheckoutLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (!isActive) {
        const referralCode = getStoredReferralCode();
        const payload: Record<string, unknown> = { usagePlanId: selectedPlanId };
        if (referralCode) payload.referralCode = referralCode;

        const { checkoutUrl } = await startBotCheckout(id, payload as any);
        window.location.href = checkoutUrl;
      } else {
        await changeBotUsagePlan(id, { usagePlanId: selectedPlanId });
        setSuccess(t("botPlan.success.planUpdatedProration"));

        const updatedBot = await getBotById(id);
        setBot(updatedBot);
      }
    } catch (err: any) {
      console.error(err);
      setError(err?.message || t("botPlan.errors.updateFailed"));
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!id) {
    return null;
  }

  const title = t(
    "botPlan.onboarding.title",
    "Choose your plan and start using Coslo"
  );
  const subtitle = t(
    "botPlan.onboarding.subtitle",
    "We’ve preconfigured your channels, bookings and Lead Ads. Now pick the plan that matches your volume."
  );

  const selectedPlan = plans.find((p) => p.id === selectedPlanId) || null;
  const combinedTotalFormatted = selectedPlan
    ? formatCurrency(selectedPlan.monthlyAmountCents, selectedPlan.currency)
    : null;

  const isActive = bot?.status === "ACTIVE";

  return (
    <OnboardingLayout
      currentStep="plan"
      botId={id}
      title={title}
      subtitle={subtitle}
      layout="full"
    >
      <div className="onb-plan-layout">
        {error && <div className="form-error onboarding-form-error-txt">{error}</div>}
        {success && (
          <div className="alert alert-success">
            <div className="alert-title">
              {t("botPlan.success.title", "Plan updated")}
            </div>
            <div className="alert-body">{success}</div>
          </div>
        )}

        {/* ENERGY / LIMITS EXPLANATION */}
        <section className="onb-plan-info">
          <div className="onb-plan-info-icon" aria-hidden="true">
            i
          </div>
          <div className="onb-plan-info-body">
            <h2 className="onb-plan-info-title">
              {t(
                "botPlan.energyInfo.title",
                "How Energy and limits work"
              )}
            </h2>
            <p style={{color:"#5b5b5b"}}>
              {t(
                "botPlan.energyInfo.main",
                "Almost every action on the platform consumes Energy (assistant messages, document retrieval, tools, ingestion). WhatsApp template messages (outbound) have their own limit and do not consume Energy."
              )}
            </p>
            <p className="muted">
              {t(
                "botPlan.energyInfo.estimates",
                "The \"Up to\" values shown in each plan are maximum estimates if you spent all your Energy on a single type of task. Actual usage varies based on message length, document size and context."
              )}
            </p>
          </div>
        </section>

        {/* CONFIGURATION SUMMARY */}
        <section className="onb-plan-summary-card">
          <header className="onb-plan-summary-header">
            <div>
              <h2>
                {t(
                  "botPlan.onboarding.summaryTitle",
                  "Configuration summary"
                )}
              </h2>
              <p className="onb-plan-summary-subtitle">
                {t(
                  "botPlan.onboarding.summarySubtitle",
                  "Here’s what you’ve configured for this bot so far."
                )}
              </p>
            </div>
            {bot && (
              <span className="onb-plan-status-pill">
                {t("botPlan.summary.statusLabel", "Status:")}{" "}
                <strong>{bot.status}</strong>
              </span>
            )}
          </header>

          {loading || !bot ? (
            <p className="muted">{t("common.loading", "Loading…")}</p>
          ) : (
            <ul className="onb-plan-summary-list">
              <li>
                <span className="onb-plan-summary-label">
                  {t(
                    "botPlan.onboarding.summary.channels",
                    "Channels"
                  )}
                </span>
                <span className="onb-plan-summary-value">
                  {["WEB", "WHATSAPP", "FACEBOOK", "INSTAGRAM"]
                    .filter((type) => channelEnabled(type as ChannelType))
                    .join(", ") ||
                    t(
                      "botPlan.onboarding.summary.none",
                      "None configured"
                    )}
                  {" · "}
                  <Link
                    to={`/onboarding/bots/${encodeURIComponent(
                      bot.id
                    )}/channels`}
                  >
                    {t("botPlan.onboarding.edit", "Edit")}
                  </Link>
                </span>
              </li>
              <li>
                <span className="onb-plan-summary-label">
                  {t(
                    "botPlan.onboarding.summary.booking",
                    "Bookings"
                  )}
                </span>
                <span className="onb-plan-summary-value">
                  {bookingEnabled
                    ? t(
                        "botPlan.onboarding.summary.enabled",
                        "Enabled"
                      )
                    : t(
                        "botPlan.onboarding.summary.disabled",
                        "Disabled"
                      )}
                  {" · "}
                  <Link
                    to={`/onboarding/bots/${encodeURIComponent(
                      bot.id
                    )}/booking`}
                  >
                    {t("botPlan.onboarding.edit", "Edit")}
                  </Link>
                </span>
              </li>
              <li>
                <span className="onb-plan-summary-label">
                  {t(
                    "botPlan.onboarding.summary.leadAds",
                    "Lead Ads → WhatsApp"
                  )}
                </span>
                <span className="onb-plan-summary-value">
                  {leadAdsEnabled
                    ? t(
                        "botPlan.onboarding.summary.enabled",
                        "Enabled"
                      )
                    : t(
                        "botPlan.onboarding.summary.disabled",
                        "Disabled"
                      )}
                  {" · "}
                  <Link
                    to={`/onboarding/bots/${encodeURIComponent(
                      bot.id
                    )}/lead-ads`}
                  >
                    {t("botPlan.onboarding.edit", "Edit")}
                  </Link>
                </span>
              </li>
            </ul>
          )}
        </section>

        {/* PLANS + PRICING */}
        <div className="onb-plan-grid">
          {/* LEFT: plans */}
          <section className="onb-plan-card">
            <header className="onb-plan-card-header">
              <h2>{t("botPlan.plans.title", "Plans")}</h2>
              <p className="onb-plan-card-subtitle">
                {t(
                  "botPlan.plans.subtitle",
                  "All features are available in every plan. Choose how much monthly Energy you want. Estimates below show what you could get if you spent all Energy on a single type of task."
                )}
              </p>
            </header>

            {plansLoading && (
              <p className="muted">
                {t("botPlan.plans.loading", "Loading plans…")}
              </p>
            )}
            {plansError && <p className="form-error onboarding-form-error-txt">{plansError}</p>}

            {!plansLoading && plans.length === 0 && !plansError && (
              <p className="muted">
                {t("botPlan.plans.empty", "No plans available.")}
              </p>
            )}

            {plans.length > 0 && (
              <div className="onb-plan-cards">
                {plans.map((plan) => {
                  const isSelected = plan.id === selectedPlanId;
                  const features = getPlanFeatures(plan);
                  const isMostPopular = isMostPopularPlan(plan);
                  return (
                    <button
                      key={plan.id}
                      type="button"
                      className={
                        "onb-plan-card-item" +
                        (isSelected ? " onb-plan-card-item--selected" : "")
                      }
                      onClick={() => handleSelectPlan(plan.id)}
                    >
                      <div className="onb-plan-card-inner">
                        <div className="onb-plan-card-copy">
                          <div className="onb-plan-card-header-row">
                            <div className="onb-plan-card-name">
                              {plan.name}
                            </div>
                            {isMostPopular && (
                              <span className="onb-plan-badge-popular">
                                {t(
                                  "botPlan.plans.mostPopular",
                                  "Most popular"
                                )}
                              </span>
                            )}
                          </div>

                          <div className="onb-plan-card-price">
                            {formatCurrency(
                              plan.monthlyAmountCents,
                              plan.currency
                            )}
                            <span className="onb-plan-card-price-period">
                              /{t("botPlan.perMonth", "month")}
                            </span>
                          </div>

                          {plan.description && (
                            <p className="onb-plan-card-description">
                              {plan.description}
                            </p>
                          )}

                          <ul className="onb-plan-card-meta">
                            <li>
                              <span className="onb-plan-meta-label">
                                {t(
                                  "botPlan.plans.tokensPerMonthLabel",
                                  "Energy per month"
                                )}
                                <span
                                  className="onb-plan-meta-info"
                                  aria-label={t(
                                    "botPlan.energyInfo.tooltip",
                                    "Energy is a unified credit that covers assistant messages, retrieval and ingestion. WhatsApp template messages have a separate limit."
                                  )}
                                  title={t(
                                    "botPlan.energyInfo.tooltip",
                                    "Energy is a unified credit that covers assistant messages, retrieval and ingestion. WhatsApp template messages have a separate limit."
                                  )}
                                >
                                  i
                                </span>
                              </span>
                              :{" "}
                              <strong>
                                {formatEnergyDisplay(plan.monthlyTokens)}
                              </strong>
                            </li>
                          </ul>

                          {/* Feature list inside the card */}
                          <ul className="onb-plan-features">
                            {features.map((feature) => (
                              <li
                                key={feature.id}
                                className="onb-plan-feature"
                              >
                                <span
                                  className={
                                    "onb-plan-feature-icon " +
                                    (feature.available
                                      ? "onb-plan-feature-icon--ok"
                                      : "onb-plan-feature-icon--off")
                                  }
                                >
                                  {feature.available ? "✓" : "✕"}
                                </span>
                                <span className="onb-plan-feature-text">
                                  {feature.label}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          {/* RIGHT: price breakdown */}
          <section className="onb-plan-card onb-plan-card--side">
            <header className="onb-plan-card-header">
              <h2>
                {t("botPlan.summary.title", "Monthly cost summary")}
              </h2>
              <p className="onb-plan-card-subtitle">
                {t(
                  "botPlan.summary.subtitle",
                  "Selected plan, Energy and estimated total."
                )}
              </p>
            </header>

            <h3 className="onb-plan-side-heading">
              {t("botPlan.summary.selectedPlan", "Selected plan")}
            </h3>

            {selectedPlan ? (
              <div className="onb-plan-summary-lines">
                <div className="onb-plan-summary-line">
                  <span className="onb-plan-summary-line-label">
                    {selectedPlan.name}
                  </span>
                  <span className="onb-plan-summary-line-amount">
                    {formatCurrency(
                      selectedPlan.monthlyAmountCents,
                      selectedPlan.currency
                    )}
                    /{t("botPlan.perMonth", "month")}
                  </span>
                </div>
                <div className="onb-plan-summary-line">
                  <span className="onb-plan-summary-line-label">
                    {t(
                      "botPlan.plans.tokensPerMonthLabel",
                      "Energy per month"
                    )}
                    <span
                      className="onb-plan-meta-info"
                      aria-label={t(
                        "botPlan.energyInfo.tooltip",
                        "Energy is a unified credit that covers assistant messages, retrieval and ingestion. WhatsApp template messages have a separate limit."
                      )}
                      title={t(
                        "botPlan.energyInfo.tooltip",
                        "Energy is a unified credit that covers assistant messages, retrieval and ingestion. WhatsApp template messages have a separate limit."
                      )}
                    >
                      i
                    </span>
                  </span>
                  <span className="onb-plan-summary-line-amount">
                    {formatEnergyDisplay(selectedPlan.monthlyTokens)}
                  </span>
                </div>
              </div>
            ) : (
              <p className="muted">
                {t(
                  "botPlan.summary.noPlanSelected",
                  "Select a plan on the left to see details."
                )}
              </p>
            )}

            {selectedPlan && (
              <ul className="onb-plan-features onb-plan-features--summary">
                {getPlanFeatures(selectedPlan).map((feature) => (
                  <li key={feature.id} className="onb-plan-feature">
                    <span
                      className={
                        "onb-plan-feature-icon " +
                        (feature.available
                          ? "onb-plan-feature-icon--ok"
                          : "onb-plan-feature-icon--off")
                      }
                    >
                      {feature.available ? "✓" : "✕"}
                    </span>
                    <span className="onb-plan-feature-text">
                      {feature.label}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <div className="onb-plan-summary-total onb-plan-summary-total--strong">
              <span>
                {t(
                  "botPlan.summary.totalPerMonth",
                  "Estimated total per month"
                )}
              </span>
              <strong>
                {combinedTotalFormatted ??
                  t(
                    "botPlan.summary.selectPlanToSeeTotal",
                    "Select a plan to see the total"
                  )}
              </strong>
            </div>
          </section>
        </div>

        {/* BOTTOM ACTIONS */}
        <div
          className="onboarding-form-actions"
          style={{ marginTop: "1.2rem" }}
        >
          <button
            className="btn-secondary btn-color"
            type="button"
            onClick={handleBack}
            disabled={checkoutLoading}
          >
            {t("common.back", "Back")}
          </button>
          <button
            className="btn-primary"
            type="button"
            onClick={handleActivateOrChangePlan}
            disabled={checkoutLoading || !selectedPlan}
          >
            {checkoutLoading
              ? isActive
                ? t("botPlan.actions.updating", "Updating…")
                : t("botPlan.actions.redirecting", "Redirecting…")
              : isActive
              ? t("botPlan.actions.changePlan", "Change plan")
              : t("botPlan.actions.activatePay", "Activate & pay")}
          </button>
        </div>
      </div>
    </OnboardingLayout>
  );
};

export default OnboardingPlanPage;
